import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get server time in Brunei timezone
export const getServerTime = async () => {
  const response = await axios.get(`${API}/server-time`);
  return response.data;
};

export const bookingsAPI = {
  checkAvailability: async (locationId, date, timeSlot, duration = 1) => {
    const response = await axios.get(`${API}/availability/${locationId}/${date}/${timeSlot}`, {
      params: { duration },
    });
    return response.data;
  },

  createBooking: async (bookingData) => {
    const response = await axios.post(`${API}/bookings`, bookingData);
    return response.data;
  },

  getUserBookings: async (userId) => {
    const response = await axios.get(`${API}/bookings/user/${userId}`);
    return response.data;
  },

  getGuestBookings: async (phone) => {
    const response = await axios.get(`${API}/bookings/guest/${phone}`);
    return response.data;
  },

  getBooking: async (bookingId) => {
    const response = await axios.get(`${API}/bookings/${bookingId}`);
    return response.data;
  },

  cancelBooking: async (bookingId, { reason = null } = {}) => {
    // Customer-side cancel. `reason` is optional and lands on the booking
    // doc + cancellation email. Default body works for callers that
    // don't capture a reason.
    const response = await axios.post(`${API}/bookings/${bookingId}/cancel`, { reason });
    return response.data;
  },

  processPayment: async (bookingId, paymentData) => {
    const response = await axios.post(`${API}/bookings/${bookingId}/payment`, paymentData);
    return response.data;
  },

  downloadCalendar: async (bookingId) => {
    const response = await axios.get(`${API}/bookings/${bookingId}/calendar`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getEquipment: async () => {
    const response = await axios.get(`${API}/equipment`);
    return response.data;
  },

  joinWaitlist: async (waitlistData) => {
    const response = await axios.post(`${API}/waitlist`, waitlistData);
    return response.data;
  },
  
  getUserWaitlist: async (userId) => {
    const response = await axios.get(`${API}/waitlist/user/${userId}`);
    return response.data;
  },
  
  getGuestWaitlist: async (phone) => {
    const response = await axios.get(`${API}/waitlist/guest/${phone}`);
    return response.data;
  },
  
  cancelWaitlistEntry: async (entryId) => {
    const response = await axios.delete(`${API}/waitlist/${entryId}`);
    return response.data;
  }
};

export const locationsAPI = {
  getLocations: async () => {
    const response = await axios.get(`${API}/locations`);
    return response.data;
  },
  
  getAllLocations: async () => {
    const response = await axios.get(`${API}/locations/all`);
    return response.data;
  },
  
  getLocation: async (locationId) => {
    const response = await axios.get(`${API}/locations/${locationId}`);
    return response.data;
  },
  
  createLocation: async (locationData) => {
    const response = await axios.post(`${API}/locations`, locationData);
    return response.data;
  },
  
  updateLocation: async (locationId, locationData) => {
    const response = await axios.put(`${API}/locations/${locationId}`, locationData);
    return response.data;
  },
  
  deleteLocation: async (locationId) => {
    const response = await axios.delete(`${API}/locations/${locationId}`);
    return response.data;
  }
};

export const adminAPI = {
  login: async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    return response.data;
  },

  verifyMFA: async (mfaToken, otp) => {
    const response = await axios.post(`${API}/admin/verify-mfa`, { 
      mfa_token: mfaToken, 
      otp 
    });
    return response.data;
  },

  resendMFA: async (mfaToken) => {
    const response = await axios.post(`${API}/admin/resend-mfa`, { 
      mfa_token: mfaToken 
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await axios.post(`${API}/admin/forgot-password`, { email });
    return response.data;
  },

  verifyResetOtp: async (resetToken, otp) => {
    const response = await axios.post(`${API}/admin/verify-reset-otp`, {
      reset_token: resetToken,
      otp,
    });
    return response.data;
  },

  resendResetOtp: async (resetToken) => {
    const response = await axios.post(`${API}/admin/resend-reset-otp`, {
      reset_token: resetToken,
    });
    return response.data;
  },

  resetPassword: async (authToken, newPassword) => {
    const response = await axios.post(`${API}/admin/reset-password`, {
      reset_authorization_token: authToken,
      new_password: newPassword,
    });
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await axios.get(`${API}/admin/dashboard/stats`);
    return response.data;
  },

  getPendingBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/pending?${params.toString()}`);
    return response.data;
  },

  assignCourts: async (bookingId, courtsAssigned, discountType = null, discountValue = 0, discountReason = '') => {
    // Build discount object if discount is applied
    let discount = null;
    if (discountType && discountType !== 'none' && discountValue > 0) {
      discount = {
        type: discountType,
        value: parseFloat(discountValue),
        reason: discountReason || 'No reason provided'
      };
    }
    
    const response = await axios.post(`${API}/admin/bookings/assign-courts`, {
      booking_id: bookingId,
      courts_assigned: courtsAssigned,
      discount: discount
    });
    return response.data;
  },

  cancelBooking: async (bookingId, { voidPayment = false, reason = null } = {}) => {
    // `void_payment=true` triggers the hybrid auto-void on the backend —
    // for same-day Pocket Pay charges the gateway is reversed in the same
    // request that cancels the booking.
    // `reason` is optional and lands in booking.cancellation_reason +
    // the customer email (if provided).
    const response = await axios.put(
      `${API}/admin/bookings/${bookingId}/cancel${voidPayment ? '?void_payment=true' : ''}`,
      { reason }
    );
    return response.data;
  },

  voidPocketPay: async (bookingId, { reason = null } = {}) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/void-pocketpay`, { reason });
    return response.data;
  },

  searchBookingForRelease: async (ref) => {
    const response = await axios.get(`${API}/admin/bookings/release/search`, { params: { ref } });
    return response.data;
  },

  releasePaidBooking: async (bookingId, reason = '') => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/release-paid`, {
      reason: (reason || '').trim() || null,
    });
    return response.data;
  },

  checkVoidEligibility: async (bookingId) => {
    const response = await axios.get(`${API}/admin/bookings/${bookingId}/void-eligibility`);
    return response.data;
  },

  // PR-2 pay-later staff actions
  markBookingPaid: async (bookingId, payload = {}) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/mark-paid`, payload);
    return response.data;
  },

  releaseHeldBooking: async (bookingId, payload = {}) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/release`, payload);
    return response.data;
  },

  sendPaymentReminder: async (bookingId) => {
    const response = await axios.post(`${API}/admin/bookings/${bookingId}/send-payment-reminder`);
    return response.data;
  },

  getAwaitingPaymentBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/awaiting-payment?${params.toString()}`);
    return response.data;
  },

  getConfirmedBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/confirmed?${params.toString()}`);
    return response.data;
  },

  // Unified admin bookings feed (excludes PENDING_ALLOCATION, which lives on Pending Requests).
  // Paginated bookings list with server-side filters.
  // See booking_routes.py::get_all_admin_bookings for the shape:
  //   - When `page_size` is set → { rows, total, page, page_size, has_more }
  //   - When neither `page` nor `page_size` set → bare array (legacy)
  // The frontend always passes both, so we always get the object shape.
  getBookings: async ({
    date_from, date_to, phone, email, booking_ref,
    status, source, page = 1, page_size = 50,
  } = {}) => {
    const params = new URLSearchParams();
    // Only include truthy filter values so the backend defaults kick in.
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    if (phone) params.append('phone', phone);
    if (email) params.append('email', email);
    if (booking_ref) params.append('booking_ref', booking_ref);
    if (status && status !== 'ALL') params.append('status', status);
    if (source && source !== 'ALL') params.append('source', source);
    params.append('page', String(page));
    params.append('page_size', String(page_size));
    const response = await axios.get(`${API}/admin/bookings/all?${params.toString()}`);
    return response.data;
  },

  // Chip counts — per-status and per-source, respecting text/date
  // filters but not status/source (each dimension counts across the
  // other, so chips honestly preview what toggling would yield).
  getBookingsCounts: async ({ date_from, date_to, phone, email, booking_ref } = {}) => {
    const params = new URLSearchParams();
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    if (phone) params.append('phone', phone);
    if (email) params.append('email', email);
    if (booking_ref) params.append('booking_ref', booking_ref);
    const response = await axios.get(`${API}/admin/bookings/all/counts?${params.toString()}`);
    return response.data;
  },

  // Today + tomorrow glance widgets — filter-independent snapshot.
  getBookingsGlance: async () => {
    const response = await axios.get(`${API}/admin/bookings/glance`);
    return response.data;
  },

  getAllAdminBookings: async (dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/bookings/all?${params.toString()}`);
    return response.data;
  },

  getWaitlist: async (status, locationId, dateFrom, dateTo) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (locationId) params.append('location_id', locationId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await axios.get(`${API}/admin/waitlist?${params.toString()}`);
    return response.data;
  },

  getWaitlistStats: async () => {
    const response = await axios.get(`${API}/admin/waitlist/stats`);
    return response.data;
  },

  notifyWaitlistUser: async (entryId, method = 'sms', message = '') => {
    const response = await axios.post(`${API}/admin/waitlist/${entryId}/notify?method=${method}`, { message });
    return response.data;
  },

  confirmWaitlistBooking: async (entryId) => {
    const response = await axios.post(`${API}/admin/waitlist/${entryId}/confirm`, {});
    return response.data;
  },

  cancelWaitlistEntry: async (entryId, reason = '') => {
    const response = await axios.post(`${API}/admin/waitlist/${entryId}/cancel`, { reason });
    return response.data;
  },

  expireWaitlistEntry: async (entryId) => {
    const response = await axios.post(`${API}/admin/waitlist/${entryId}/expire`, {});
    return response.data;
  },

  // Booking Management
  createManualBooking: async (payload) => {
    const response = await axios.post(`${API}/admin/bookings/manual`, payload);
    return response.data;
  },

  // Customer Management
  getCustomers: async () => {
    const response = await axios.get(`${API}/admin/customers`);
    return response.data;
  },

  getCustomerDetails: async (customerId) => {
    const response = await axios.get(`${API}/admin/customers/${customerId}`);
    return response.data;
  },

  // Activation toggle — PATCH /api/admin/users/{id}/status. Idempotent;
  // returns { user, changed, from_status, to_status }. RBAC-gated to
  // customers.write (admin + manager + staff per Feb 2026 matrix).
  updateCustomerStatus: async (userId, status, reason) => {
    const response = await axios.patch(`${API}/admin/users/${userId}/status`, { status, reason });
    return response.data;
  },

  // Guest profile edit — PATCH /api/admin/customers/guest_<phone>.
  // Only `email` is editable in v1 (name/phone stay fixed). Server
  // hard-rejects registered users with 403. Returns fresh detail payload.
  updateGuestProfile: async (customerId, payload) => {
    const response = await axios.patch(`${API}/admin/customers/${customerId}`, payload);
    return response.data;
  },

  // Staff Management
  getStaff: async () => {
    const response = await axios.get(`${API}/admin/staff`);
    return response.data;
  },

  getStaffMember: async (staffId) => {
    const response = await axios.get(`${API}/admin/staff/${staffId}`);
    return response.data;
  },

  createStaff: async (staffData) => {
    const response = await axios.post(`${API}/admin/staff`, staffData);
    return response.data;
  },

  updateStaff: async (staffId, staffData) => {
    const response = await axios.put(`${API}/admin/staff/${staffId}`, staffData);
    return response.data;
  },

  deleteStaff: async (staffId) => {
    const response = await axios.delete(`${API}/admin/staff/${staffId}`);
    return response.data;
  },

  getPricingRules: async (locationId) => {
    const response = await axios.get(`${API}/admin/pricing/${locationId}`);
    return response.data;
  },

  createPricingRule: async (rule) => {
    const response = await axios.post(`${API}/admin/pricing`, rule);
    return response.data;
  },

  updatePricingRule: async (ruleId, rule) => {
    const response = await axios.put(`${API}/admin/pricing/${ruleId}`, rule);
    return response.data;
  },

  deletePricingRule: async (ruleId) => {
    const response = await axios.delete(`${API}/admin/pricing/${ruleId}`);
    return response.data;
  }
};

export const seedData = async () => {
  const response = await axios.post(`${API}/seed-data`);
  return response.data;
};


// Security API (MFA Policy Management)
export const securityAPI = {
  getMFAPolicies: async () => {
    const response = await axios.get(`${API}/admin/security/mfa-policy`);
    return response.data;
  },

  updateMFAPolicy: async (roleName, mfaEnabled) => {
    const response = await axios.patch(`${API}/admin/security/mfa-policy/${roleName}`, { mfa_enabled: mfaEnabled });
    return response.data;
  }
};

// ==================== BRUNEI HOLIDAYS API ====================
// Backed by /api/admin/holidays/* — used by Settings → Holidays page.
export const holidaysAPI = {
  list: async ({ year, activeOnly = false } = {}) => {
    const params = {};
    if (year) params.year = year;
    if (activeOnly) params.active_only = true;
    const response = await axios.get(`${API}/admin/holidays`, { params });
    return response.data;
  },

  check: async (date) => {
    const response = await axios.get(`${API}/admin/holidays/check/${date}`);
    return response.data;
  },

  create: async (payload) => {
    const response = await axios.post(`${API}/admin/holidays`, payload);
    return response.data;
  },

  update: async (id, payload) => {
    const response = await axios.put(`${API}/admin/holidays/${id}`, payload);
    return response.data;
  },

  remove: async (id, { hard = false } = {}) => {
    const response = await axios.delete(`${API}/admin/holidays/${id}`, {
      params: hard ? { hard: true } : {},
    });
    return response.data;
  },

  importPreset: async (year) => {
    const response = await axios.post(`${API}/admin/holidays/import-preset/${year}`, {});
    return response.data;
  },

  duplicateYear: async (fromYear, toYear) => {
    const response = await axios.post(
      `${API}/admin/holidays/duplicate-year`,
      {},
      { params: { from_year: fromYear, to_year: toYear } }
    );
    return response.data;
  },
};


// ==================== BOOKING SERIES (RECURRING) ====================

export const seriesAPI = {
  previewSeries: async (payload) => {
    const res = await axios.post(`${API}/admin/bookings/series/preview`, payload);
    return res.data;
  },
  createSeries: async (payload) => {
    const res = await axios.post(`${API}/admin/bookings/series`, payload);
    return res.data;
  },
  listSeries: async (params = {}) => {
    const res = await axios.get(`${API}/admin/bookings/series`, { params });
    return res.data;
  },
  getSeries: async (seriesId) => {
    const res = await axios.get(`${API}/admin/bookings/series/${seriesId}`);
    return res.data;
  },
  cancelSeries: async (seriesId, payload = {}) => {
    const res = await axios.post(`${API}/admin/bookings/series/${seriesId}/cancel`, payload);
    return res.data;
  },
};
